import type { AuthProvider } from '@refinedev/core';
import type { PublicUser } from '@residenone/shared';
import { api, clearSelectedClusterId, getSelectedClusterId } from './lib/api-client';
import { getClerkToken, signOutOfClerk } from './lib/clerk-auth';

function responseStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status ??
    (error as { statusCode?: number; status?: number })?.statusCode ??
    (error as { status?: number })?.status;
}

export async function currentUser(): Promise<PublicUser> {
  try {
    return (await api.get<{ user: PublicUser }>('/auth/me')).data.user;
  } catch (error) {
    if (responseStatus(error) !== 403 || !getSelectedClusterId()) throw error;
    clearSelectedClusterId();
    return (await api.get<{ user: PublicUser }>('/auth/me')).data.user;
  }
}

export const authProvider: AuthProvider = {
  async login() {
    // The Clerk <SignIn /> component owns the sign-in flow.
    return { success: true, redirectTo: '/dashboard' };
  },

  async logout() {
    clearSelectedClusterId();
    await signOutOfClerk();
    return { success: true, redirectTo: '/login' };
  },

  async check() {
    const token = await getClerkToken();
    if (!token) return { authenticated: false, redirectTo: '/login', logout: true };

    try {
      const user = await currentUser();
      if (user.role !== 'STAFF') return { authenticated: false, redirectTo: '/access' };
      return { authenticated: true };
    } catch (error) {
      const status = responseStatus(error);
      if (status === 401) {
        await signOutOfClerk();
        return { authenticated: false, redirectTo: '/login', logout: true };
      }
      if (status === 403 || status === 409) return { authenticated: false, redirectTo: '/access' };
      // A transient API/D1 outage must not discard a valid Clerk session.
      return { authenticated: true };
    }
  },

  async onError(error) {
    const status = (error as { statusCode?: number; status?: number })?.statusCode ??
      (error as { status?: number })?.status;
    if (status === 401) {
      await signOutOfClerk();
      return { logout: true, redirectTo: '/login' };
    }
    return {};
  },

  async getIdentity() {
    if (!(await getClerkToken())) return null;
    try {
      const user = await currentUser();
      return {
        id: user.id,
        name: user.full_name,
        email: user.email,
        avatar: user.avatar_url ?? undefined,
        role: user.role,
        app_role: user.app_role,
        permissions: user.permissions ?? [],
        unit: user.unit,
        cluster_id: user.cluster_id,
        admin_scope: user.admin_scope,
      };
    } catch {
      return null;
    }
  },

  async getPermissions() {
    if (!(await getClerkToken())) return null;
    try {
      return (await currentUser()).permissions ?? [];
    } catch {
      return null;
    }
  },
};
